import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  fetchCart,
  updateCartQuantity,
  removeFromCart,
  applyCoupon,
  clearCoupon,
} from "../store/cartSlice";
import "./CartPage.css";

const CartPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { token } = useSelector(state => state.auth);
  const { items, subtotal, gstTotal, grandTotal, coupon, discount, error } = useSelector((state) => state.cart);
  const [couponCode, setCouponCode] = useState("");

  const getUserId = () => {
    const userProfile = localStorage.getItem('user_profile');
    if (userProfile) {
      try {
        const parsed = JSON.parse(userProfile);
        return parsed.id || parsed.user_id;
      } catch (e) {
        console.error("Failed to parse user profile context", e);
        return null;
      }
    }
    return null;
  };

  const computedSubtotal = items ? items.reduce((acc, item) => acc + (item.price * item.quantity), 0) : 0;

  const computedGstTotal = items ? items.reduce((acc, item) => {
    const itemTaxPercent = item.gst_percent !== undefined ? Number(item.gst_percent) : 18;
    const itemTotalBasePrice = item.price * item.quantity;
    const itemGstAmount = (itemTotalBasePrice * itemTaxPercent) / 100;
    return acc + itemGstAmount;
  }, 0) : 0;

  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      dispatch(fetchCart(userId));
    }
  }, [dispatch]);

  const handleQtyChange = (cartId, currentQty, delta) => {
    const targetQty = currentQty + delta;
    const userId = getUserId();
    
    if (targetQty < 1) {
      dispatch(removeFromCart({ cartId, userId }));
      return;
    }
    
    dispatch(updateCartQuantity({ cartId, quantity: targetQty, userId }));
  };

  const handleRemoveItem = (cartId) => {
    const userId = getUserId();
    dispatch(removeFromCart({ cartId, userId }));
  };

  const finalSubtotal = subtotal || computedSubtotal;
  const finalGstTotal = gstTotal || computedGstTotal;
  const finalGrandTotal = Math.max(0, (finalSubtotal + finalGstTotal) - discount);

  // 🚀 FIXED: कूपन कोड को भी राउटर स्टेट के साथ आगे पास किया ताकि Checkout Page इसे API पेलोड में भेज सके
  const handleProceedcheck = () => {
    if (!token) {
      window.dispatchEvent(new CustomEvent('TRIGGER_AUTH_MODAL', { detail: { mode: 'login' } }));
      return;
    }
    
    // अब हम ग्रैंड टोटल के साथ-साथ कूपन कोड (अगर अप्लाई हुआ है तो) भी भेज रहे हैं
    navigate('/checkout', { 
      state: { 
        checkoutTotal: finalGrandTotal,
        appliedCouponCode: coupon || null // रिडक्स स्टेट से एक्टिव कूपन का नाम लिया
      } 
    });
  };

  const handleCouponApply = (e) => {
    e.preventDefault();
    const cleanCode = couponCode.trim().toUpperCase();
    if (!cleanCode) return;

    const baseTotalBeforeDiscount = finalSubtotal + finalGstTotal;
    dispatch(applyCoupon({ code: cleanCode, orderTotal: baseTotalBeforeDiscount }));
  };

  if (!items || items.length === 0) {
    return (
      <div className="cart-empty-state">
        <h2>Your Shopping Cart is Empty.</h2>
      </div>
    );
  }

  return (
    <div className="cart-container">
      <h1 className="cart-header">Shopping Cart</h1>
      <div className="cart-layout">
        <div className="cart-items-section">
          {items.map((item) => (
            <div key={item.cart_id} className="cart-item-row-div">
              <div className="cart-item-row">
                <div>
                  <img
                    src={`https://rbuymart.rbhardwaj.com/server/${item.primary_image}`}
                    alt={item.name}
                    className="cart-item-img"
                  />
                  <div className="cart-item-qty">
                    <button onClick={() => handleQtyChange(item.cart_id, item.quantity, -1)}>-</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => handleQtyChange(item.cart_id, item.quantity, 1)}>+</button>
                  </div>
                </div>

                <div className="cart-item-details">
                  <h3>{item.name}</h3>
                  {item.color && (
                    <p className="item-meta">Variant: {item.color} | {item.size}</p>
                  )}

                  <div className="price-box" style={{borderBottom:'none'}}>
                    <section>
                     
                        <sup className="price-parsent">{item.gst_percent !== undefined ? item.gst_percent : 18}% GST</sup>
                     
                      <h2 className="current-price">
                        ₹{(item.price * item.quantity).toLocaleString("en-IN")}
                      </h2>
                    </section>
                    <section>
                      <small>Inclusive of all taxes</small>
                    </section>
                  </div>
                </div>
              </div>

              <button onClick={() => handleRemoveItem(item.cart_id)} className="btn-remove">
                Remove
              </button>
            </div>
          ))}
        </div>

        <div className="cart-summary-section">
          <div className="summary-card">
            <h3>Order Summary</h3>
            
            <div className="summary-row">
              <span>Subtotal (Net)</span>
              <b>₹{finalSubtotal.toLocaleString("en-IN")}</b>
            </div>

            <div className="summary-row">
              <span>GST</span>
              <b>₹{finalGstTotal.toLocaleString("en-IN")}</b>
            </div>

            {discount > 0 && (
              <div className="summary-row discount-row">
                <span>Coupon ({coupon})</span>
                <b>- ₹{discount.toLocaleString("en-IN")}</b>
              </div>
            )}
            <hr />

            <div className="summary-row total-row">
              <span>Estimated Grand Total</span>
              <span>₹{finalGrandTotal.toLocaleString("en-IN")}</span>
            </div>

            <form onSubmit={handleCouponApply} className="coupon-form">
              <input
                type="text"
                placeholder="Enter Coupon Code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                disabled={!!coupon}
              />
              {coupon ? (
                <button
                  type="button"
                  onClick={() => {
                    dispatch(clearCoupon());
                    setCouponCode("");
                  }}
                  className="btn-coupon-clear"
                >
                  Remove
                </button>
              ) : (
                <button type="submit" className="btn-coupon-apply">Apply</button>
              )}
            </form>
            {error && <p className="cart-error-msg">{error}</p>}
            <button
              className="btn-checkout-proceed"
              onClick={handleProceedcheck}
            >
              Proceed to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;